'use client';

import crypto from 'crypto';
import Cookies from 'js-cookie';
import {SKZMatch} from '@sdk-mobile';
import {promoCodeEventEmitter} from '@/app/(app)/games/[gid]/(main)/store/_src/usePromoCode';
import {DeviceInfo, PlayerMetadata, Tournament} from '@/services/internal/tournament/types';
import {getDeviceInfo} from '@/services/internal/device/getDeviceInfo';
import {getUserId} from '@/services/internal/user/getUserId';
import getGameId from '@/utils/getGameId';
import countryList from '@/utils/countries.json';
import {customAlert} from '@/components/atoms/CustomAlert';
import {getStoredSettings} from '@/services/internal/settings/client';
import {getCurrentPosition} from '@/utils/location';
import {isiOS, isSafariOS, isMacOS} from '@/utils/device';
import resetPassword from '@/services/internal/auth/resetPassword';
import {FTUE_STATE, REGISTER_LOCK_COOKIE_NAME, SKILLZ_APP_COOKIE_NAME} from '@/utils/constant';
import Preferences from '@/sdk-mobile-web/Preferences/Preferences.web';
import getTempPassword from '@/services/internal/auth/getTempPassword';
import auth from '@/services/internal/auth/AuthQueue';
import {addBrowserSideAppHeaders} from '@/utils/unityHeaders';
import {setFtueCookie} from '@/utils/ftueCookieHelper';
import cleanupUserCache from '@/utils/cleanupUserCache';
type Callback = (error: Error | null, data: any) => void;

type FetchDynamicDeviceInfoCallback = (
  deviceInfo: DeviceInfo,
  playerMetadata: PlayerMetadata
) => void;

export type MeasureLayoutOnSuccessCallback = (
  left: number,
  top: number,
  width: number,
  height: number
) => void;

const regionLocales = ['zh-hans', 'pt-br', 'en-in'];

function getLocale() {
  if (typeof navigator === 'undefined') return 'en';

  const locale = navigator.languages?.[0]?.toLocaleLowerCase() || 'en';
  if (regionLocales.includes(locale)) {
    return locale;
  }
  return locale.split('-')[0];
}

function redirectToHomeScreen() {
  const currentBaseUrl = window.location.origin;
  const currentPath = window.location.pathname;
  const splitPath = currentPath.split('/');
  const urlPart = `/${splitPath[1]}/${splitPath[2]}`;
  if (window.location.href.includes('live-event')) {
    window.location.reload();
  } else {
    window.location.href = `${currentBaseUrl}${urlPart}`;
  }
}

async function startLocationManager({message}: {title: string; message: string}) {
  console.log('GEO: startLocationManager called with message:', message);
  const geoLocationPermission = await navigator?.permissions
    ?.query({name: 'geolocation'})
    .then((permission) => {
      console.log('GEO: Permission query result:', permission.state);
      return permission.state;
    });

  const isSkillzApp = Cookies.get(SKILLZ_APP_COOKIE_NAME) === '1';
  console.log('GEO: Is Skillz App:', isSkillzApp);

  let accessDeniedMessage =
    'You have denied access to your location. Please enable location access in your browser or app settings. Then click OK to reload the page.';
  console.log('GEO: Platform checks - iOS:', isiOS(), 'MacOS:', isMacOS(), 'Safari:', isSafariOS());
  if (isSkillzApp && isiOS()) {
    console.log('GEO: Using iOS Skillz app specific message');
    accessDeniedMessage = `
You have denied access to your location.
Go to Settings → Privacy & Security → Location Services → Skillz App → Location, select While Using the App or Always. Then click OK to reload the page.`;
  } else if ((isMacOS() && isSafariOS()) || (isiOS() && isSafariOS())) {
    console.log('GEO: Using Safari specific message');
    accessDeniedMessage = `
Please follow the steps below to enable location services for Safari:

Step 1:
Go to Settings → Privacy & Security → Location Services → Safari Websites, then choose Allow.

Step 2:
Go to Settings → Apps → Safari → Location, then choose Allow.

Then click OK to reload the page.`;
  }
  console.log('GEO: geoLocationPermission', geoLocationPermission);
  console.log('GEO: Processing permission state:', geoLocationPermission);
  switch (geoLocationPermission) {
    case 'denied':
      console.log('GEO: Permission denied - showing access denied message');
      return await customAlert('Location Access Denied', accessDeniedMessage, [
        {
          text: 'OK',
          onPress: () => {
            console.log('GEO: User acknowledged denial - redirecting to home screen');
            return redirectToHomeScreen();
          },
        },
      ]);

    case 'granted':
      console.log('GEO: Permission already granted');
      return Promise.resolve(true);

    case 'prompt':
      console.log('GEO: Showing permission prompt to user');
      return await customAlert('', message, [
        {
          text: 'Not Now',
          onPress: () => {
            console.log('GEO: User chose not to grant permission');
            return false;
          },
        },
        {
          text: 'Allow',
          onPress: async () => {
            console.log('GEO: User clicked Allow - requesting position');
            try {
              await getCurrentPosition();
            } catch (error) {
              console.log('GEO: Error getting location', {
                code: error.code,
                message: error.message,
                stack: error.stack,
              });
              let errorMessage;
              if (error.code === error.PERMISSION_DENIED) {
                console.log('GEO: User denied permission during position request');
                errorMessage = accessDeniedMessage;
              } else if (error.code === error.POSITION_UNAVAILABLE) {
                console.log('GEO: Position unavailable error');
                errorMessage = 'We are unable to confirm your location, please try again later.';
              } else {
                console.log('GEO: Unknown geolocation error');
                errorMessage =
                  'An unknown error occurred while requesting location access. Please try again.';
              }
              console.log('GEO: Showing error alert to user:', errorMessage);
              return await customAlert('Location Access Denied', errorMessage, [
                {
                  text: 'OK',
                  onPress: () => {
                    console.log('GEO: User acknowledged error - redirecting to home screen');
                    return redirectToHomeScreen();
                  },
                },
              ]);
            }
            return true;
          },
        },
      ]);

    default:
      return await customAlert(
        'Error',
        'An unknown error occurred while requesting location access. Please try again.',
        [{text: 'OK', onPress: () => false}]
      );
  }
}

const getDynamicTheme = () => {
  const settings = getStoredSettings();
  if (typeof window === 'undefined' || !settings) {
    return {};
  }

  return settings.theme?.gameTheme || {};
};

const getEnvironmentFromUrl = () => {
  if (typeof window !== 'undefined') {
    const url = window.location.href;
    if (url.includes('sandbox')) {
      return '4';
    } else if (url.includes('localhost')) {
      return '6';
    } else if (url.includes('play.skillz')) {
      return '2';
    } else if (url.includes('staging')) {
      return '3';
    } else {
      return '3';
    }
  }
  // In case window is not available (like during SSR), you can handle it differently if needed
  return '2'; // Default to prod
};

declare global {
  interface Window {
    AF?: (event: string, method: string, args: any) => void;
  }
}

const NativeModules = {
  SKZReactStyleManager: {
    ThemeJSON: JSON.stringify(getDynamicTheme() || {}),
    Theme: getDynamicTheme(),
    getDynamicTheme,
  },
  SKZCountryList: countryList,
  UIManager: {
    measure: (node: HTMLElement, cb) => {
      const {top, left, width, height} = node.getBoundingClientRect();
      // x: number, y: number, width: number, height: number, rootX: number, rootY: number
      cb(left, top, width, height, 0, 0);
    },
    measureLayout: (
      element: HTMLElement | null,
      relativeToElement: HTMLElement | null,
      errorCallback: (error: string) => void,
      successCallback: MeasureLayoutOnSuccessCallback
    ) => {
      if (!element || !relativeToElement) {
        errorCallback('Element or relativeToElement is null');
        return;
      }

      const elementRect = element.getBoundingClientRect();
      const relativeRect = relativeToElement.getBoundingClientRect();

      const x = elementRect.left - relativeRect.left;
      const y = elementRect.top - relativeRect.top;
      const width = elementRect.width;
      const height = elementRect.height;

      successCallback(x, y, width, height);
    },
  },
  SKZReactViewControllerManager: {
    popToRoot: () => {
      const gameId = getGameId();
      window.location.pathname = `/games/${gameId}/home`;
    },
  },
  SKZReactVersusManager: {
    startLocationManager,
    passControlToPublisherForJoin: () => {
      console.log('WebSDK: SKZReactVersusManager.passControlToPublisherForJoin() MOCK USED');
    },
    seedRandomForJoin: (match: SKZMatch) => {
      console.log('WebSDK: SKZReactVersusManager.seedRandomForJoin() MOCK USED', match);
    },
  },
  I18nManager: {},
  SKZAppRatingController: {
    recordMatchResultForRatingTrigger: () => {},
  },
  SKZReactSaveAccountManager: {
    executeStoredCompletion: () => {
      console.log('WebSDK: SKZReactSaveAccountManager.executeStoredCompletion() MOCK USED');
    },
  },
  SKZReactUserManager: {
    trackEvent: () => {},
    trackEventForUser: () => {},
    getUserId: (callback: Callback) => {
      try {
        callback(null, getUserId());
      } catch (e) {
        callback(e, null);
      }
    },
    getUserPass: async (callback: (error: any, pass: string | null) => void) => {
      const {error, password} = await getTempPassword();

      callback(error, password);
    },
    uploadAvatar: () => {
      console.log('WebSDK: SKZReactUserManager.uploadAvatar() MOCK USED');
    },
    sendIdentityLinkEvent: (userId: string) => {
      console.warn(
        'AppsFlyer SDK - send identity link is not shimmed on web, use AppsFlyerUtils.ts instead.',
        userId
      );
    },
    trackDepositEvent: (eventTitle, eventValue, currency) => {
      console.warn(
        'AppsFlyer SDK - trackDepositEvent is not shimmed on web, use AppsFlyerUtils.ts instead.',
        eventTitle,
        eventValue,
        currency
      );
    },
  },
  SKZCoreDataWrapper: {
    createCoreDataObject: (_tournament: Tournament, callback: () => void) => {
      callback();
    },
  },
  SKZReactLocaleManager: {
    locale: getLocale(),
  },
  SKZDepositManager: {
    showCurrencyGrantedViewWithCurrency: (cashAmount: string, zAmount: string) => {
      promoCodeEventEmitter.emit(
        'displayPromoCodeGranted',
        true,
        Number(cashAmount),
        Number(zAmount)
      );
    },
  },
  SkillzModule: {
    launchProgressionRoom: () => {
      console.log('SkillzModule.launchProgressionRoom');
    },
    getLocationFromPostalCode: () => {
      console.log('SkillzModule.getLocationFromPostalCode');
    },
    loginWithSMSEnabled: true,
    uploadImageToPresignedURL: async (
      imageBase64: string,
      signedUrl: string,
      callback: Callback
    ) => {
      try {
        const headers = await addBrowserSideAppHeaders({
          'Content-Type': 'application/json',
        });
        const response = await fetch('/api/upload-image', {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({imageBase64, signedUrl}),
        });
        console.log(`S3 upload: Image Picker call: (${response.status}) ${response.statusText}`);
        if (response.ok) {
          callback(null, true);
        } else {
          const errorMessage = await response.text();
          callback(
            new Error(`Failed to upload image: ${response.statusText} - ${errorMessage}`),
            false
          );
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        callback(error, false);
      }
    },
    loginUserWithUserName: async (
      username: string,
      password: string,
      _: any, // Not used param for web shouldArchiveUser
      resolve: (success: boolean) => void
    ) => {
      const {success} = await auth.loginUserWithUserName(username, password);
      if (success) {
        Cookies.remove(REGISTER_LOCK_COOKIE_NAME);
        setFtueCookie(FTUE_STATE.FINISHED);
        Preferences.disableFTUE();
        resolve(success);
      } else {
        resolve(success);
        await customAlert('Invalid Credentials', 'Unable to login, please try again', [
          {text: 'Okay', onPress: () => false},
        ]);
      }
    },
    loginUserWithPhoneNumber: async (
      phone: string,
      code: string,
      _: any, // Not used param for web shouldArchiveUser
      resolve: (error: unknown, success: boolean) => void
    ) => {
      const {success, error} = await auth.loginUserWithPhone(phone, code, getGameId());
      if (success) {
        Cookies.remove(REGISTER_LOCK_COOKIE_NAME);
        setFtueCookie(FTUE_STATE.FINISHED);
        Preferences.disableFTUE();
      }
      return resolve(error, success);
    },
    verifyUserWithPassword: async (
      username: string,
      password: string,
      resolve: (error: unknown, success: boolean) => void
    ) => {
      try {
        const {success} = await auth.loginUserWithUserName(username, password);
        if (success) {
          resolve(null, true);
        } else {
          resolve(new Error('Invalid password'), false);
        }
      } catch (e) {
        resolve(new Error('Invalid password'), false);
      }
    },
    sendForgotPassword: async (usernameOrEmail: string, callback: (success: boolean) => void) => {
      const resp = await resetPassword(usernameOrEmail, getGameId());
      const {success} = await resp.json();
      return callback(success);
    },
    exitSkillz: () => {
      console.log('WebSDK: SkillzModule.exitSkillz() MOCK USED');
    },
    saveAccountForSSO: (username: string, userId: string) => {
      // TODO: Save account for SSO
      console.log('WebSDK: SkillzModule.saveAccountForSSO() MOCK USED', username, userId);
    },
    playWinSound: () => {},
    shouldInteractWithInterface: (
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      callback: (shouldInteract: boolean, isMatchInProgress: boolean) => void
    ) => {},
    updateNativeUser: () => {},
    App: {
      IsGameLandscape: false,
      IsLandscape: false,
      GameHasSyncBot: false,
      get GameId() {
        return getGameId();
      },
      get IconPath() {
        return window.gameIcon;
      },
    },
    showPromoCodeGrantedCongrats: () => {},
    setupNotificationsWithOptions: () => {},
    setupNotifications: () => {},
    enterGamePresentationOrientation: () => {
      return Promise.resolve();
    },
    setSideMenuPanEnabled: () => {},
    fetchDynamicDeviceInfo: (callback: FetchDynamicDeviceInfoCallback) => {
      const {deviceInfo, playerMetadata} = getDeviceInfo();
      return callback(deviceInfo, playerMetadata);
    },
    scheduleUnfinishedGameNotification: (mSecUntilNotify: number) => {
      console.log(
        'WebSDK: SkillzModule.scheduleUnfinishedGameNotification() MOCK USED',
        mSecUntilNotify
      );
    },
    resetLocationManager: (callback: (isGranted: boolean) => void) => {
      // TODO: Reset location manager
      return callback(true);
    },
    setEnabledKeyboardManager: () => {},
    updateUserPassword: () => {},
    SERVER_URL: '/apigw',
  },
  RNRandomBytes: {
    randomBytes: (numBytes: number, cb: Callback) => {
      cb(null, crypto.randomBytes(numBytes).toString('base64'));
    },
    updateUserPassword: () => {},
  },
  SKZAPIManager: {
    SERVER_URL: '/apigw',
    OSVersion: '1.0.0',
    Type: {
      PlayAgain: 'PlayAgain',
    },
    fetchAuthCredential: async (resolve: (token: string) => void) => {
      const token = await auth.fetchAuthCredential();
      return resolve(token);
    },
    recordClickWithType: () => {},
    refreshAuthToken: async (resolve: () => void) => {
      await auth.refreshToken();
      cleanupUserCache();
      return resolve();
    },
    CurrentEnvironment: getEnvironmentFromUrl(),
    Environment: {
      Prod: 2,
      QA: 0,
      Staging: 3,
      Sandbox: 4,
      Custom: 6,
    },
    OnError: {
      promptRetry: () => {},
      failSilently: () => {},
    },
  },
};

export default NativeModules;
